import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { Chip, FormControl } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import Autocomplete from '@material-ui/lab/Autocomplete';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import CloseIcon from '@material-ui/icons/Close';
import Validation from '../../../Validation/Validation';
import {
  groupedDestinations,
  sortedDestinationsWithIcons,
} from '../../../../helpers/destinationHelper';
import {
  bindTravelSelectedPlanCoverages,
  getLimitOnRegionChange,
  getOnlyLiteDisallowedCountry,
  getRegionFromDestinations,
} from '../../../../helpers/travel/travelCalcHelpers';
import specialRegions from './options';
import Label from './Label';
import {
  ALLOWED_TRAVEL_LIMITS_BY_REGION,
  REGIONS,
  SUGGESTED_COUNTRIES,
  TRAVEL_LIMITS,
  TRAVEL_MIN_DAYS,
  TRAVEL_PLANS_LABEL,
} from '../../../../constants/travelCalcConstants';
import { filterTravelAreas } from '../../../../helpers/selectHelpers';
import CustomListbox from './CustomListbox';
import SuggestedCountries from './SuggestedCountries';
import MainTextField from '../../../MUI/MainTextField';

import useStyles from '../makeStyles';

const DestinationPicker = ({
  selectedDestinations,
  selectedLimit,
  setRegion,
  setDestinations,
  setSelectedLimit,
  setSelectedPlan,
  days,
  formProps,
  onCoverageChange,
}) => {
  const { i18n } = useTranslation();
  const classes = useStyles();
  const { errors, setValue, setError, clearError } = formProps;
  const allDestinations = useSelector(
    (store) => store.travelDestinations.allDestinations,
    shallowEqual,
  );

  const destinationsWithIcons = useMemo(
    () => sortedDestinationsWithIcons(allDestinations, i18next.language, specialRegions),
    [allDestinations],
  );

  const isError = useMemo(() => errors && !!errors.region, [errors]);

  const groupDestinations = useMemo(
    () => groupedDestinations(specialRegions, destinationsWithIcons),
    [destinationsWithIcons],
  );

  const updateRegion = useCallback(
    (newRegion, shouldValidate = false, hasDisallowedCountry) => {
      if (newRegion && !ALLOWED_TRAVEL_LIMITS_BY_REGION[newRegion].includes(selectedLimit.id)) {
        const newLimit = getLimitOnRegionChange(newRegion);
        setSelectedLimit(newLimit);
      }
      if (!hasDisallowedCountry) {
        setSelectedPlan(TRAVEL_PLANS_LABEL.ELITE);
        setSelectedLimit(TRAVEL_LIMITS['50kEur']);
        onCoverageChange(bindTravelSelectedPlanCoverages(
          TRAVEL_PLANS_LABEL.ELITE,
        ));
        if (days < TRAVEL_MIN_DAYS) {
          setError('days', 'invalid', 'validationErrors.minDays');
        }
      } else {
        setSelectedPlan(TRAVEL_PLANS_LABEL.GEORGIA);
        setSelectedLimit(TRAVEL_LIMITS['50kEur']);
        clearError(['days', 'end']);
        onCoverageChange(bindTravelSelectedPlanCoverages(
          TRAVEL_PLANS_LABEL.GEORGIA,
        ));
      }
      setValue('region', newRegion, shouldValidate);
      setRegion(newRegion);
    },
    [
      selectedLimit.id,
      setValue,
      setRegion,
      setSelectedLimit,
      setSelectedPlan,
      clearError,
      setError,
      days,
      onCoverageChange,
    ],
  );

  const handleDestinationChange = (newDestination) => {
    let updatedDestinations;

    if (Array.isArray(newDestination)) {
      updatedDestinations = newDestination;
    } else {
      const isExisting = selectedDestinations.some(
        (destination) => destination.code === newDestination.code,
      );

      if (!isExisting) {
        updatedDestinations = [...selectedDestinations, newDestination];
      } else {
        updatedDestinations = selectedDestinations.filter(
          (destination) => destination.code !== newDestination.code,
        );
      }
    }

    setDestinations(updatedDestinations);
    const newRegion = getRegionFromDestinations(updatedDestinations);
    const hasDisallowedCountry = getOnlyLiteDisallowedCountry(updatedDestinations);
    updateRegion(newRegion, true, hasDisallowedCountry);
  };

  const handleDelete = (item) => {
    const updatedValue = selectedDestinations.filter((val) => val !== item);
    handleDestinationChange(updatedValue);
  };

  const suggestedCountries = useMemo(
    () => groupDestinations.flatMap(
      (destination) => destination.options.filter(
        (item) => SUGGESTED_COUNTRIES.includes(item.code),
      ),
    ),
    [groupDestinations],
  );

  const options = useMemo(
    () => groupDestinations.flatMap((destination) => destination.options.map((item) => ({
      ...item,
      isSpecial: Object.values(REGIONS).includes(item.code),
    }))),
    [groupDestinations],
  );

  return (
    <>
      <Validation errors={errors} name="region">
        <FormControl fullWidth variant="outlined">
          <Autocomplete
            className={classes.autocompleteRoot}
            PaperComponent={({ children }) => <div className={classes.paper}>{children}</div>}
            ListboxComponent={CustomListbox}
            renderOption={(option) => (
              <div className={classes.option}>
                <Label option={option} specialRegions={specialRegions} />
              </div>
            )}
            getOptionSelected={(option, value) => option.code === value.code}
            multiple
            options={options}
            getOptionLabel={(option) => option.country[`name${i18n.language.toUpperCase()}`]}
            value={selectedDestinations}
            onChange={(_, newValue) => {
              handleDestinationChange(newValue);
            }}
            filterOptions={filterTravelAreas}
            popupIcon={<KeyboardArrowDownIcon className={classes.icon} />}
            closeIcon={<CloseIcon className={classes.removeIcon} />}
            renderInput={(params) => (
              <MainTextField
                {...params}
                required
                variant="outlined"
                label="travel.destinationPlaceholderShort"
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                  },
                }}
                error={isError}
              />
            )}
            renderTags={(value, getTagProps) => value.map((option, index) => (
              <Chip
                key={option.id}
                label={<Label option={option} specialRegions={specialRegions} />}
                {...getTagProps({ index })}
                deleteIcon={(
                  <ClearIcon
                    onMouseDown={(event) => event.stopPropagation()}
                    className={classes.clearIcon}
                  />
                )}
                clickable
                onDelete={() => {
                  handleDelete(option);
                }}
              />
            ))}
          />
        </FormControl>
      </Validation>

      <SuggestedCountries
        suggestedCountries={suggestedCountries}
        selectedDestinations={selectedDestinations}
        handleDestinationChange={handleDestinationChange}
      />
    </>
  );
};

DestinationPicker.propTypes = {
  selectedDestinations: PropTypes.arrayOf(PropTypes.shape({})),
  selectedLimit: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  setSelectedPlan: PropTypes.func.isRequired,
  setRegion: PropTypes.func.isRequired,
  setDestinations: PropTypes.func.isRequired,
  setSelectedLimit: PropTypes.func.isRequired,
  formProps: PropTypes.shape({
    setValue: PropTypes.func.isRequired,
    errors: PropTypes.shape({
      region: PropTypes.shape({}),
    }),
    setError: PropTypes.func,
    clearError: PropTypes.func,
  }).isRequired,
  days: PropTypes.number.isRequired,
  onCoverageChange: PropTypes.func.isRequired,
};

DestinationPicker.defaultProps = {
  selectedDestinations: [],
};

export default DestinationPicker;
